import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { XCircle, CheckCircle2, Clock, Ban, Building2, Mail, Calendar, BarChart3 } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from "recharts";

const STATUS_LABEL = {
  pending: "En attente",
  in_review: "En traitement",
  processed: "Traitée",
  rejected: "Rejetée",
};
const STATUS_COLOR = {
  pending: "bg-[#FFD700] text-[#0A0A0A]",
  in_review: "bg-[#002FA7] text-white",
  processed: "bg-[#059669] text-white",
  rejected: "bg-[#E60000] text-white",
};

const CAT_COLORS = {
  prix: "#E60000",
  service: "#D97706",
  besoin: "#868E96",
  concurrent: "#002FA7",
  autre: "#0A0A0A",
};

export default function AdminCancellations() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const [newStatus, setNewStatus] = useState("in_review");

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, { data: s }] = await Promise.all([
        api.get("/admin/cancellations"),
        api.get("/admin/cancellations/stats"),
      ]);
      setItems(data || []);
      setStats(s || null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = items.filter((x) => filter === "all" || x.status === filter);
  const counts = items.reduce((acc, x) => { acc[x.status] = (acc[x.status] || 0) + 1; return acc; }, {});

  const save = async () => {
    if (!selected) return;
    try {
      await api.patch(`/admin/cancellations/${selected.id}`, { status: newStatus, admin_note: note.trim() || null });
      toast.success(`Statut mis à jour : ${STATUS_LABEL[newStatus]}`);
      setSelected(null);
      setNote("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-6xl" data-testid="admin-cancellations-page">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.3em] text-[#868E96] font-bold mb-2">// Résiliations</div>
        <h1 className="font-display text-4xl font-black tracking-tighter">Demandes d&apos;annulation</h1>
        <p className="text-sm text-[#495057] mt-2">
          Traitez les demandes des chefs manuellement côté Stripe. Une fois côté PAG marquée <b>Traitée</b>, l&apos;entreprise passe en statut <b>warning</b> (accès conservé jusqu&apos;à fin de période).
        </p>
      </div>

      {/* Stats Résiliations — graphes */}
      {stats && stats.total > 0 && (
        <div className="mb-8 grid lg:grid-cols-3 gap-4" data-testid="cancel-stats-section">
          {/* KPIs */}
          <div className="lg:col-span-1 border border-[#0A0A0A]">
            <div className="p-4 bg-[#0A0A0A] text-white flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-[#FFD700]" />
              <span className="text-xs uppercase tracking-[0.25em] font-bold">Vue synthétique</span>
            </div>
            <div className="grid grid-cols-2 gap-0" data-testid="cancel-stats-kpis">
              <div className="p-4 border-r border-b border-[#DEE2E6]">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#868E96]">Total</div>
                <div className="font-display text-3xl font-black mt-1">{stats.total}</div>
              </div>
              <div className="p-4 border-b border-[#DEE2E6]">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#FFD700]">En attente</div>
                <div className="font-display text-3xl font-black mt-1">{(stats.by_status.find(s => s.status === "pending") || {}).count || 0}</div>
              </div>
              <div className="p-4 border-r border-[#DEE2E6]">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#059669]">Traitées</div>
                <div className="font-display text-3xl font-black mt-1">{(stats.by_status.find(s => s.status === "processed") || {}).count || 0}</div>
              </div>
              <div className="p-4">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#E60000]">Refusées</div>
                <div className="font-display text-3xl font-black mt-1">{(stats.by_status.find(s => s.status === "rejected") || {}).count || 0}</div>
              </div>
            </div>
          </div>

          {/* Graph : motifs de résiliation */}
          <div className="lg:col-span-2 border border-[#0A0A0A] bg-white" data-testid="cancel-stats-chart">
            <div className="p-4 bg-[#002FA7] text-white flex items-center justify-between">
              <div className="flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-[#FFD700]" />
                <span className="text-xs uppercase tracking-[0.25em] font-bold">Motifs de résiliation (classification auto)</span>
              </div>
              <span className="text-[10px] text-white/70">Détection par mots-clés</span>
            </div>
            <div className="p-4" style={{ height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.by_category} margin={{ top: 12, right: 12, left: 0, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#DEE2E6" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#495057" }} interval={0} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#495057" }} />
                  <Tooltip cursor={{ fill: "#F8F9FA" }} contentStyle={{ border: "1px solid #0A0A0A", borderRadius: 0, fontSize: 12 }} />
                  <Bar dataKey="count" name="Nombre" radius={[0, 0, 0, 0]}>
                    {stats.by_category.map((entry) => (
                      <Cell key={entry.category} fill={CAT_COLORS[entry.category] || "#0A0A0A"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* Filter tabs */}
      <div className="flex flex-wrap gap-2 mb-6" data-testid="cancel-filters">
        {["pending", "in_review", "processed", "rejected", "all"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            data-testid={`filter-${f}`}
            className={`px-4 py-2 text-xs uppercase tracking-wider font-bold border-2 transition-colors ${filter === f ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#DEE2E6] hover:border-[#0A0A0A]"}`}
          >
            {f === "all" ? "Toutes" : STATUS_LABEL[f]} {counts[f] > 0 && <span className="ml-1 opacity-70">({counts[f]})</span>}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-[#868E96]">Chargement…</div>
      ) : filtered.length === 0 ? (
        <div className="border-2 border-dashed border-[#DEE2E6] p-12 text-center" data-testid="cancellations-empty">
          <Ban className="h-12 w-12 mx-auto text-[#868E96] mb-4" />
          <div className="text-lg font-semibold mb-2">Aucune demande {filter !== "all" ? STATUS_LABEL[filter].toLowerCase() : ""}</div>
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map((req) => (
            <div key={req.id} data-testid={`cancel-row-${req.id}`} className="border border-[#DEE2E6] bg-white p-5 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between gap-4 mb-3">
                <div className="flex items-start gap-3 flex-1">
                  <Building2 className="h-5 w-5 text-[#495057] flex-shrink-0 mt-1" />
                  <div className="flex-1">
                    <div className="font-display text-lg font-black">{req.entreprise_name || "—"}</div>
                    <div className="text-xs text-[#495057] font-mono">{req.entreprise_code}</div>
                    <div className="text-xs text-[#495057] mt-1 flex items-center gap-3 flex-wrap">
                      <span className="flex items-center gap-1"><Mail className="h-3 w-3" /> {req.chef_email}</span>
                      <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {new Date(req.created_at).toLocaleDateString("fr-FR")}</span>
                      {req.effective_date && <span>Souhaite le : <b>{new Date(req.effective_date).toLocaleDateString("fr-FR")}</b></span>}
                    </div>
                  </div>
                </div>
                <span className={`px-3 py-1 text-[10px] font-bold uppercase tracking-wider whitespace-nowrap ${STATUS_COLOR[req.status]}`}>
                  {STATUS_LABEL[req.status]}
                </span>
              </div>
              <div className="mt-3 p-3 bg-[#F8F9FA] text-sm">
                <div className="text-[10px] uppercase tracking-wider font-bold text-[#868E96] mb-1">Motif</div>
                {req.reason}
              </div>
              {req.admin_note && (
                <div className="mt-2 p-3 border-l-2 border-[#002FA7] bg-[#002FA7]/5 text-xs">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-[#002FA7] mb-1">Note admin</div>
                  {req.admin_note}
                </div>
              )}
              {req.stripe_subscription_id && (
                <div className="mt-3 text-[10px] text-[#868E96] font-mono">Subscription : {req.stripe_subscription_id}</div>
              )}
              <div className="mt-3 flex gap-2 flex-wrap">
                {req.status !== "processed" && req.status !== "rejected" && (
                  <>
                    <Button
                      size="sm"
                      onClick={() => { setSelected(req); setNote(req.admin_note || ""); setNewStatus("in_review"); }}
                      data-testid={`treat-${req.id}`}
                      className="h-9 bg-[#002FA7] text-white"
                    >
                      <Clock className="h-3 w-3 mr-1" /> Traiter
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => { setSelected(req); setNote(req.admin_note || ""); setNewStatus("processed"); }}
                      data-testid={`process-${req.id}`}
                      className="h-9 bg-[#059669] text-white"
                    >
                      <CheckCircle2 className="h-3 w-3 mr-1" /> Marquer résiliée
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => { setSelected(req); setNote(req.admin_note || ""); setNewStatus("rejected"); }}
                      data-testid={`reject-${req.id}`}
                      className="h-9 border-[#E60000] text-[#E60000]"
                    >
                      <XCircle className="h-3 w-3 mr-1" /> Refuser
                    </Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg" data-testid="cancel-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-black">
              {newStatus === "processed" ? "Confirmer résiliation" : newStatus === "rejected" ? "Refuser la demande" : "Mettre en traitement"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {selected && (
              <div className="p-3 bg-[#F8F9FA] text-sm">
                <div className="font-semibold">{selected.entreprise_name}</div>
                <div className="text-xs text-[#495057] font-mono">{selected.entreprise_code}</div>
              </div>
            )}
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] font-semibold">Note interne (optionnelle)</Label>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} data-testid="admin-note" className="mt-1" placeholder="Ex : Résiliation traitée côté Stripe le X/X. Accès conservé jusqu'à Y." />
            </div>
            {newStatus === "processed" && (
              <div className="text-xs bg-[#FFFDF2] border-l-4 border-[#FFD700] p-3">
                ⚠️ Cette action passe l&apos;entreprise en statut <b>warning</b>. N&apos;oubliez pas de résilier l&apos;abonnement Stripe côté dashboard PAG.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelected(null)}>Annuler</Button>
            <Button
              onClick={save}
              data-testid="confirm-status"
              className={newStatus === "processed" ? "bg-[#059669] text-white" : newStatus === "rejected" ? "bg-[#E60000] text-white" : "bg-[#002FA7] text-white"}
            >
              Confirmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
